if ( typeof SearchForm !== 'function' ) {

	class SearchForm extends HTMLElement {

		constructor() {

			super();
			this.cachedResults = {};
			this.input = this.querySelector('[data-js-search-input]');
			this.predictiveSearchResults = this.querySelector('[data-js-search-results]');
			this.overlay = this.querySelector('.search-results-overlay');
			this.results = [];
			this.a11yIndex = -1;
			this.lastQuery = '';
			this.controller = null;

			// Esc chiude sempre la ricerca, con o senza testo nella barra
			this.input.addEventListener('keydown', e=>{
				if ( e.keyCode === window.KEYCODES.ESC ) {
					e.preventDefault();
					this.clearSearchResults();
				}
			});

			if ( KROWN.settings.predictive_search_enabled != "false" ) {

				// 'input' scatta a ogni modifica del testo, anche da tastiera del telefono, incolla e dettatura
				const onType = debounce(()=>{
					const query = this.input.value.trim();
					if ( query === this.lastQuery ) {
						return;
					}
					this.lastQuery = query;
					if ( query === '' ) {
						this.emptySearchResults();
					} else {
						this.getSearchResults(query);
					}
				}, 120);

				this.input.addEventListener('input', onType);

				// Tornando nella barra con del testo gia' scritto, i risultati ricompaiono subito
				this.input.addEventListener('focus', onType);

				// Se si e' gia' scritto qualcosa prima che questo script fosse pronto, la ricerca parte subito
				if ( this.input.value.trim() !== '' ) {
					onType();
				}

				this.input.addEventListener('keydown', e=>{
					if ( this.a11yIndex != -1 ) {
						if (
							e.keyCode === window.KEYCODES.UP ||
							e.keyCode === window.KEYCODES.DOWN ||
							e.keyCode === window.KEYCODES.RETURN
						) {
							e.preventDefault();
						}
					}
				});

				this.input.addEventListener('keyup', e=>{
					if (e.keyCode === window.KEYCODES.UP) {
						this.navigateThrough('UP');
						return true;
					}

					if (e.keyCode === window.KEYCODES.DOWN) {
						this.navigateThrough('DOWN');
						return true;
					}

					if (e.keyCode === window.KEYCODES.RETURN) {
						if ( this.a11yIndex != -1 && this.results[this.a11yIndex] ) {
							document.location.href = this.results[this.a11yIndex].tagName == "A" ? this.results[this.a11yIndex].href : this.results[this.a11yIndex].querySelector('a').href;
						}
					}
				})

			}

		}

		getSearchResults(query="") {

			const queryKey = query.toLowerCase();
			this.a11yIndex = -1;

			// Una richiesta ancora in viaggio per un testo ormai superato viene annullata
			if ( this.controller ) {
				this.controller.abort();
				this.controller = null;
			}

			if ( this.cachedResults[queryKey] ) {
				this.renderSearchResults(this.cachedResults[queryKey]);
				return;
			}

			this.preloadSearchResults();

			// Fino a 10 risultati per tipo: cosi' i suggerimenti non tolgono posto ai prodotti
			const controller = new AbortController();
			this.controller = controller;

			fetch(`${KROWN.settings.routes.predictive_search_url}?q=${encodeURIComponent(query)}&section_id=helper-predictive-search&resources[limit]=10&resources[limit_scope]=each`, { signal: controller.signal })
				.then(response=>{
					if (!response.ok) {
						throw new Error(response.status);
					}
					return response.text();
				})
				.then(text=>{
					const parser = new DOMParser().parseFromString(text, 'text/html');
					const section = parser.querySelector('#shopify-section-helper-predictive-search');

					// Se troviamo la section, usiamo il suo innerHTML, altrimenti usiamo tutto il testo (fragment)
					const results = section ? section.innerHTML : text;

					if ( this.controller === controller ) {
						this.controller = null;
					}

					if ( results.trim() === "" ) {
						this.emptySearchResults();
						return;
					}

					this.cachedResults[queryKey] = results;

					// Si mostra solo la risposta che corrisponde a cio' che c'e' scritto adesso
					if ( this.input.value.trim().toLowerCase() === queryKey ) {
						this.renderSearchResults(results);
					}
				})
				.catch(e=>{
					if ( e.name === 'AbortError' ) {
						return;
					}
					this.predictiveSearchResults.classList.remove('is-loading');
					console.error('Ricerca non riuscita:', e);
				});

		}

		// Svuota il pannello ma lascia aperta la ricerca (velo e barra attivi)
		emptySearchResults(){
			if ( this.controller ) {
				this.controller.abort();
				this.controller = null;
			}
			this.predictiveSearchResults.innerHTML = '';
			this.predictiveSearchResults.classList.remove('is-loading');
			this.predictiveSearchResults.style.display = 'none';
			document.body.style.overflow = '';
			this.results = [];
			this.a11yIndex = -1;
		}

		// Chiude del tutto la ricerca: pannello, velo e tastiera
		clearSearchResults(){
			this.emptySearchResults();
			this.lastQuery = '';
			document.querySelectorAll('.search-results-overlay').forEach(overlay => overlay.classList.remove('active'));
			if ( document.activeElement === this.input ) {
				this.input.blur();
			}
		}

		showSearchResults(){
			this.predictiveSearchResults.style.setProperty('display', 'block', 'important');
			this.predictiveSearchResults.style.setProperty('opacity', '1', 'important');
			this.predictiveSearchResults.style.setProperty('visibility', 'visible', 'important');
			if ( this.overlay ) {
				this.overlay.classList.add('active');
			}
			document.body.style.overflow = 'hidden';
			// Su mobile il pannello arriva fino in fondo allo schermo: gli serve sapere da dove parte
			const top = Math.max(0, Math.round(this.predictiveSearchResults.getBoundingClientRect().top));
			this.predictiveSearchResults.style.setProperty('--nh-search-top', `${top}px`);
		}

		// Durante l'attesa i risultati precedenti restano a schermo, senza sfarfallii
		preloadSearchResults(){
			if ( this.predictiveSearchResults.innerHTML.trim() === '' ) {
				this.predictiveSearchResults.innerHTML = '<p class="nh-search-loading">Ricerca in corso…</p>';
			}
			this.predictiveSearchResults.classList.add('is-loading');
			this.showSearchResults();
		}

		renderSearchResults(results){
			this.predictiveSearchResults.innerHTML = results;
			this.predictiveSearchResults.classList.remove('is-loading');
			this.predictiveSearchResults.scrollTop = 0;
			this.showSearchResults();
			this.results = this.predictiveSearchResults.querySelectorAll('[data-js-search-item]');
		}

		navigateThrough(direction){

			if ( ! this.results.length ) {
				return;
			}

			if ( this.a11yIndex == -1 ) {
				this.a11yIndex = 0;
			} else {
				this.results[this.a11yIndex].classList.remove('active');
				if ( direction == 'UP' ) {
					if ( this.a11yIndex - 1 >= -1 ) {
						this.a11yIndex--;
					}
				} else if ( direction == 'DOWN' ) {
					if ( this.a11yIndex + 1 < this.results.length ) {
						this.a11yIndex++;
					}
				}
			}

			if ( this.a11yIndex >= 0 ) {
				this.results[this.a11yIndex].classList.add('active');
				this.results[this.a11yIndex].scrollIntoView({ block: 'nearest' });
			} else {
				this.predictiveSearchResults.scrollTop = 0;
			}

		}

	}

  if ( typeof customElements.get('search-form') == 'undefined' ) {
		customElements.define('search-form', SearchForm);
	}

}
